import { useEffect, useState, useRef } from "react";

/**
 * SGBReturnPill — schmaler Switch-Button am rechten Rand zu patrice.studio.
 *
 * - Dezent, halbe Größe der Vorversion.
 * - Schließen-X (Session-merkt sich Dismiss).
 * - Whisper-Tooltip: 1600ms, fade in / hold / fade out — wie ein sanftes Aufleuchten.
 * - Erstes Auftauchen nach 750ms scroll-idle löst den Whisper automatisch aus.
 * - Linkt extern zu https://patrice.studio (neuer Tab).
 */
const DISMISS_KEY = "sgb_return_pill_dismissed_v2";

export const SGBReturnPill = () => {
  const [visible, setVisible] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [autoWhisper, setAutoWhisper] = useState(false);
  const hasWhisperedRef = useRef(false);
  const idleTimerRef = useRef(null);
  const whisperTimerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setPastHero(y > 380);
      setVisible(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setVisible(true), 750);
    };

    idleTimerRef.current = setTimeout(() => setVisible(true), 1200);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (whisperTimerRef.current) clearTimeout(whisperTimerRef.current);
    };
  }, []);

  const show = visible && pastHero && !dismissed;

  // Trigger automatic whisper exactly once, when the pill first becomes visible.
  useEffect(() => {
    if (show && !hasWhisperedRef.current) {
      hasWhisperedRef.current = true;
      whisperTimerRef.current = setTimeout(() => {
        setAutoWhisper(true);
        // Reset so future hovers can re-trigger via CSS hover state.
        whisperTimerRef.current = setTimeout(() => setAutoWhisper(false), 1700);
      }, 400);
    }
  }, [show]);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      className="group fixed right-0 top-1/2 -translate-y-1/2 z-40 select-none"
      style={{
        opacity: show ? 1 : 0,
        transform: `translate(${show ? "0" : "12px"}, -50%)`,
        transition: "opacity 700ms ease-out, transform 700ms cubic-bezier(.2,.7,.2,1)",
        pointerEvents: show ? "auto" : "none",
      }}
    >
      {/* Whisper tooltip — hover OR auto-trigger on first appearance.
          Total animation: 1600ms (fade-in ~280ms, hold ~1040ms, fade-out ~280ms). */}
      <span
        data-testid="sgb-return-pill-tooltip"
        className={`pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-3 whitespace-nowrap px-3 py-1.5 text-canvas opacity-0 group-hover:animate-[pillWhisper_1600ms_ease-out_forwards] ${autoWhisper ? "animate-[pillWhisper_1600ms_ease-out_forwards]" : ""}`}
        style={{
          background: "rgba(40, 0, 27, 0.88)",
          border: "1px solid rgba(255, 113, 0, 0.45)",
          fontFamily: "'Cormorant Garamond', serif",
          fontStyle: "italic",
          fontSize: "0.88rem",
          letterSpacing: "0.01em",
          boxShadow: "0 8px 32px rgba(40,0,27,0.4)",
          backdropFilter: "blur(8px)",
        }}
      >
        Ins Studio. Meine andere Welt
        <span className="ml-1" style={{ color: "#FF7100" }}>→</span>
      </span>

      {/* The pill — half-sized */}
      <a
        href="https://patrice.studio"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="sgb-return-pill"
        aria-label="Ins Studio – patrice.studio"
        className="flex items-center gap-2 py-3 pl-2 pr-1 backdrop-blur-md border border-warmth/30 border-r-0 hover:border-warmth/70 transition-all duration-500"
        style={{
          background: "rgba(40, 0, 27, 0.78)",
          borderTopLeftRadius: "999px",
          borderBottomLeftRadius: "999px",
        }}
      >
        <span
          aria-hidden
          className="block w-1 h-1 rounded-full"
          style={{ background: "#FF7100" }}
        />
        <span
          className="font-mono text-[0.48rem] tracking-[0.32em] uppercase text-canvas/95 group-hover:text-canvas transition-colors"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          patrice.studio
        </span>
        <span
          aria-hidden
          className="font-mono text-canvas/80 group-hover:text-canvas transition-all duration-500 group-hover:-translate-y-0.5"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: "0.48rem" }}
        >
          ↗
        </span>
      </a>

      {/* Close X — small, sits at the top of the pill */}
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="sgb-return-pill-close"
        aria-label="Schließen"
        className="absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center border border-warmth/40 backdrop-blur-md hover:border-warmth/80 hover:scale-110 transition-all"
        style={{
          background: "rgba(40, 0, 27, 0.95)",
          fontSize: "0.6rem",
          color: "rgba(250, 247, 242, 0.75)",
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
};

export default SGBReturnPill;
